import { useMemo } from 'react';
import { useUser } from '../store/userStore';
import { Permission } from '../types/user.d';

/**
 * 사용자 권한 관리를 위한 훅 (통합 개선 버전)
 */
export const usePermission = () => {
  const user = useUser();

  /**
   * 사용자의 권한 목록을 반환 (개별 권한 우선, 역할 권한 보조)
   */
  const permissions = useMemo((): Permission[] => {
    if (!user) {
      return [];
    }

    // 1. 사용자 개별 권한 수집 (우선순위 1)
    let userPermissions: Permission[] = [];
    if (user?.permissions?.length && user.permissions.length > 0) {
      userPermissions = user.permissions
        .map((permission: any): Permission => {
          if (permission && typeof permission === 'object' && permission.name) {
            return {
              name: permission.name,
              displayName: permission.displayName || permission.name,
              _id: permission._id,
              domain: permission.domain,
              action: permission.action,
              targetType: permission.targetType,
              description: permission.description,
              source: permission.source,
              roleName: permission.roleName,
            };
          }

          // 문자열인 경우 객체로 변환
          if (typeof permission === 'string') {
            return {
              name: permission,
              displayName: permission,
            };
          }

          // 기타 경우 기본값 반환
          return {
            name: permission?.toString() || '',
            displayName: permission?.toString() || '',
          };
        })
        .filter((p: Permission) => p.name);
    }

    // 2. 역할 권한 수집 (우선순위 2) - 개별 권한이 없을 때만
    let rolePermissions: Permission[] = [];
    if (
      userPermissions.length === 0 &&
      user?.roles?.length &&
      user.roles.length > 0
    ) {
      const allRolePermissions = user.roles.flatMap(
        (role: any) => role.permissions || []
      );
      rolePermissions = allRolePermissions
        .map((permission: any): Permission => {
          if (permission && typeof permission === 'object' && permission.name) {
            return {
              name: permission.name,
              displayName: permission.displayName || permission.name,
              _id: permission._id,
              domain: permission.domain,
              action: permission.action,
              targetType: permission.targetType,
              description: permission.description,
              source: 'role',
            };
          }

          if (typeof permission === 'string') {
            return {
              name: permission,
              displayName: permission,
              source: 'role',
            };
          }

          return {
            name: permission?.toString() || '',
            displayName: permission?.toString() || '',
            source: 'role',
          };
        })
        .filter((p: Permission) => p.name);
    }

    const finalPermissions =
      userPermissions.length > 0 ? userPermissions : rolePermissions;

    return finalPermissions;
  }, [user?.permissions, user?.roles]);

  /**
   * 권한 이름 배열 반환
   */
  const permissionNames = useMemo((): string[] => {
    return permissions.map((p) => p.name);
  }, [permissions]);

  /**
   * 특정 권한 보유 여부 확인
   */
  const hasPermission = useMemo(() => {
    return (permissionName: string): boolean => {
      if (!permissions.length) {
        return false;
      }

      // 새로운 권한 시스템에 맞게 체크
      const has = permissions.some((permission: Permission) => {
        // 권한이 객체인 경우 name 필드 확인
        if (permission && typeof permission === 'object' && permission.name) {
          return permission.name === permissionName;
        }
        // 권한이 문자열인 경우 직접 비교
        if (typeof permission === 'string') {
          return permission === permissionName;
        }
        return false;
      });
      return has;
    };
  }, [permissions]);

  /**
   * 여러 권한 중 하나라도 보유 여부 확인 (OR 조건)
   */
  const hasAnyPermission = useMemo(() => {
    return (permissionName: string[]): boolean => {
      if (!permissions.length || !permissionName.length) {
        return false;
      }

      return permissionName.some((name) => hasPermission(name));
    };
  }, [hasPermission, permissions]);

  /**
   * 모든 권한 보유 여부 확인 (AND 조건)
   */
  const hasAllPermissions = useMemo(() => {
    return (permissionName: string[]): boolean => {
      if (!permissions.length || !permissionName.length) {
        return false;
      }

      return permissionName.every((name) => hasPermission(name));
    };
  }, [hasPermission, permissions]);

  /**
   * 사용자가 관리자인지 확인
   */
  const isAdmin = useMemo(() => {
    if (!user?.roles) {
      return false;
    }

    return user.roles.some((role: any) => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return ['admin', 'developer', 'ceo'].includes(roleName?.toLowerCase());
    });
  }, [user?.roles]);

  /**
   * 사용자가 파트너 관리자인지 확인
   */
  const isPartnerAdmin = useMemo(() => {
    if (!user?.roles) {
      return false;
    }

    return user.roles.some((role: any) => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return ['partner-admin', 'partner_admin'].includes(
        roleName?.toLowerCase()
      );
    });
  }, [user?.roles]);

  return {
    permissions,
    permissionNames,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isPartnerAdmin,
    totalPermissions: permissions.length,
  };
};

export default usePermission;
